// src/services/cancelarComisionesEnCascada.js
//
// F6 — Cancelación en cascada de CxPs de comisión cuando una venta entra
// en devolución.
//
// Cuando el admin inicia una devolución sobre una deuda, las CxPs de comisión
// que aún no han sido pagadas deben cancelarse automáticamente: la venta no
// se completó, por lo que los hitos pendientes de cobro ya no aplican.
// Los hitos ya pagados NO se tocan (el dinero ya salió de caja).
//
// GARANTÍAS:
//   • Solo cancela CxPs con tipoCosto=="comision_venta" y saldoRestante > 0.
//   • No toca hitos con hito.pagado === true.
//   • Idempotente: CxPs ya canceladas se ignoran silenciosamente.
//   • No bloquea el flujo de devolución — errores se capturan sin relanzar.

import {
  collection, doc, getDoc, getDocs, updateDoc,
  query, where, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";
import { cancelarCxp } from "./cxpDeudaService";
import { registrarAuditoria } from "../helpers/auditoria";

/**
 * Cancela las CxPs de comisión pendientes de pago vinculadas a una deuda,
 * y marca la comisión como cancelada.
 *
 * @param {string} deudaId  — ID del documento en `deudas`
 * @param {string} [motivo] — Motivo de cancelación (aparece en la CxP)
 * @returns {Promise<{ canceladas: number, errores: string[] }>}
 */
export async function cancelarComisionesEnCascada(deudaId, motivo = "Venta revertida por devolución") {
  const resultado = { canceladas: 0, errores: [] };

  try {
    // ── 1. Comisiones vinculadas a esta deuda ────────────────────────────
    const snap = await getDocs(
      query(collection(db, "comisiones"), where("deudaId", "==", deudaId))
    );
    if (snap.empty) return resultado;

    for (const comisDoc of snap.docs) {
      const comision = { id: comisDoc.id, ...comisDoc.data() };

      // Ya cancelada → saltar
      if (comision.estado === "cancelada") continue;

      const hitos = Array.isArray(comision.hitos) ? comision.hitos : [];
      const hitosActualizados = hitos.map((h) => ({ ...h }));
      let algoCancelado = false;

      // ── 2. Cancelar CxPs de hitos devengados y no pagados ─────────────
      for (let i = 0; i < hitos.length; i++) {
        const hito = hitos[i];

        // Si ya está pagado → respetar (el dinero ya salió)
        if (hito.pagado) continue;

        // Si tiene CxP devengada y no pagada → cancelar
        if (hito.devengado && hito.cxpId) {
          try {
            // Verificar que la CxP exista y tenga saldo (no ya cancelada)
            const cxpSnap = await getDoc(doc(db, "cxp_deudas", hito.cxpId));
            if (cxpSnap.exists()) {
              const cxp = cxpSnap.data();
              const yaCanc = cxp.cancelada === true || cxp.estado === "cancelada";
              const yaPagada = Number(cxp.saldoRestante || 0) <= 0;

              if (!yaCanc && !yaPagada) {
                await cancelarCxp(hito.cxpId, motivo);
                resultado.canceladas += 1;
              }
            }
          } catch (cxpErr) {
            resultado.errores.push(
              `Comisión ${comision.id} hito "${hito.etiqueta}": no se pudo cancelar CxP ${hito.cxpId} — ${cxpErr.message}`
            );
            continue;
          }

          // Marcar hito como cancelado en el plan.
          // OJO: si el hito ya estaba devengado (cxp creada y luego cancelada),
          // limpiamos devengado=false y NO lo contamos en el total devengado
          // del doc — la CxP fue cancelada arriba, así que el devengo dejó de
          // existir. Si solo queremos preservar la historia, queda registrada
          // en el doc cxp_deudas (estado=cancelada) y en auditoria.
          const eraDevengado = !!hito.devengado;
          hitosActualizados[i] = {
            ...hito,
            devengado: false,
            cancelado: true,
            motivoCancelacion: motivo,
            ...(eraDevengado ? { _devengadoAntesDeCancelar: true } : {}),
          };
          algoCancelado = true;
        }
      }

      // ── 3a. Detectar comisión PAGADA en deuda cancelada ──────────────
      //     Si la deuda se canceló pero el comisionista YA cobró parte de su
      //     comisión, queda un saldo "por recobrar o perdonar" — bandera para
      //     el dashboard.
      const totalPagado = Number(comision.pagado || 0);
      const hayRecobroPendiente = totalPagado > 0;

      // ── 3. Actualizar el doc de comisión ─────────────────────────────
      // Saldo "vivo" = lo que aún se le adeuda al comisionista por esta venta.
      // Una comisión cancelada NO le adeuda nada: si ya cobró algo se trata
      // por recobroPendiente; lo pendiente de devengar deja de aplicar.
      const update = {
        hitos: hitosActualizados,
        estado: "cancelada",
        motivoCancelacion: motivo,
        saldo: 0,
        devengado: 0,
        updatedAt: serverTimestamp(),
      };
      if (hayRecobroPendiente) {
        update.recobroPendiente = {
          monto: totalPagado,
          estado: "pendiente",          // "pendiente" | "perdonado" | "recuperado"
          motivoCancelacion: motivo,
          deudaIdOrigen: deudaId,
          fechaCancelacion: Timestamp.now(),
          montoRecuperado: 0,
        };
      }
      try {
        await updateDoc(doc(db, "comisiones", comision.id), update);
      } catch (updErr) {
        resultado.errores.push(
          `No se pudo actualizar comisión ${comision.id}: ${updErr.message}`
        );
        continue;
      }

      // ── 4. Auditoría (no fatal) ───────────────────────────────────────
      if (algoCancelado) {
        registrarAuditoria({
          accion: "comisiones.cancelarEnCascada",
          entidadTipo: "comision",
          entidadId: comision.id,
          detalle: `Comisión cancelada por devolución de deuda ${deudaId}`,
          despues: {
            deudaId,
            motivo,
            hitosAfectados: hitosActualizados.filter((h) => h.cancelado).length,
          },
        }).catch((e) => console.warn("Audit cancelarEnCascada error:", e));
      }
    }
  } catch (globalErr) {
    console.error("cancelarComisionesEnCascada error global:", globalErr);
    resultado.errores.push(`Error inesperado: ${globalErr.message}`);
  }

  return resultado;
}
